import { Log } from '@artup/utilities'
import { Account, Feature, Session, Permission, SecurityRequest, SecurityProfile, Preference, Preferences } from '@artup/model'
import { APIClient } from './api-client'
import { APISession } from '../api-session'

/**
 * Accounts API client
 */
export class AccountsAPIClient extends APIClient {
  /**
   * Returns API name served by this client
   */
  get name () {
    return 'Accounts'
  }

  /**
   * Pings the Accounts API.
   * Used to refresh the accounts token during account inaction,
   * to keep the session alive.
   * @returns {String} Refreshed session token
   */
  async ping () {
    try {
      const url = this.endpoint('accounts', 'ping')
      const { token } = await this.request({ url, progress: false, retry: false })
      return token
    } catch (error) {
      try {
        this.handleError(error)
      } catch (error) {
        // If ping failed with 401, this means our token has truly expired.
        // This can happen when computer has been in sleep mode for longer time,
        // or network wasn't available for whatever reason. In such case we
        // just need to log out, so we return nothing but throw no error either.
        const mustLogout = this.isAuthenticationError(error) || this.isNetworkError(error)
        if (!mustLogout) {
          throw error
        }
      }
    }
  }

  /**
   * Retrieves application features hierarchy
   * @returns {Promise<Features>}
   */
  async getFeatures () {
    try {
      const url = this.endpoint('features')
      const { features } = await this.request({ url })
      return this.asFeature(features)
    } catch (error) {
      this.handleError(error)
      return []
    }
  }

  /**
   * Retrieves the security profiles which can be assigned to avatars
   * @returns {Promise<Array[SecurityProfile]>}
   */
  async getSecurityProfiles () {
    try {
      const url = this.endpoint('security-profiles')
      const { securityProfiles } = await this.request({ url })
      return (securityProfiles || []).map(item => new SecurityProfile(item))
    } catch (error) {
      this.handleError(error)
      return []
    }
  }

  /**
   * Retrieves the list of accounts
   * @returns {Promise<Array[Account]>}
   */
  async getAccounts () {
    try {
      const url = this.endpoint('account')
      const { accounts } = await this.request({ url })
      return this.asAccounts(accounts)
    } catch (error) {
      this.handleError(error)
      return []
    }
  }

  /**
   * Retrieves the specified account
   * @param {String} id Account identifier
   * @returns {Promise<Account>}
   */
  async getAccount ({ id } = {}) {
    try {
      const url = this.endpoint('account', id)
      const { account } = await this.request({ url })
      return this.asAccount(account)
    } catch (error) {
      this.handleError(error)
    }
  }

  /**
   * Returns account by name
   * @param {String} name Account name
   * @returns {Promise<Account>}
   */
  async getAccountByName ({ name } = {}) {
    try {
      const url = this.endpoint('account', 'name', name)
      const { account } = await this.request({ url }) || {}
      return this.asAccount(account)
    } catch (error) {
      this.handleError(error)
    }
  }

  /**
   * Checks if the specified account exists
   * @param {String} name Account name
   * @returns {Promise<Boolean>}
   */
  async accountExists ({ name } = {}) {
    try {
      const url = this.endpoint('account', name, 'exists')
      const { id, exists } = await this.request({ url }) || {}
      return { id, exists }
    } catch (error) {
      this.handleError(error)
      return false
    }
  }

  /**
   * Updates the specified account
   * @param {Account} data Account to save
   * @returns {Promise<Account>} Updated account
   */
  async saveAccount ({ account } = {}) {
    try {
      // Cleanup some runtime properties, no need to transfer them over the wire,
      // their identifiers are sufficient
      const data = { ...account }
      delete data.account

      const { id } = data
      const method = id == null ? 'post' : 'put'
      const url = this.endpoint('account', id)
      const { account: savedAccount } = await this.request({ method, url, data })
      return this.asAccount(savedAccount)
    } catch (error) {
      // Let the UI handle validation errors
      if (this.isBadRequestError(error)) {
        return this.getErrorResponse(error)
      } else {
        this.handleError(error)
      }
    }
  }

  /**
   * Deletes the specified account
   * @param {Account} data Account to delete
   * @returns {Promise<Account>} Deleted account
   */
  async deleteAccount ({ account: { id } } = {}) {
    try {
      const method = 'delete'
      const url = this.endpoint('account', id)
      const { account } = await this.request({ method, url })
      return this.asAccount(account)
    } catch (error) {
      this.handleError(error)
    }
  }

  /**
   * Authenticates the account, returns session containing account details,
   * accounts and their permissions, session identifier etc.
   * @param {String} name Account name
   * @param {String} password Password
   * @param {String} reCaptchaToken reCaptcha token obtained from Google reCaptcha service during interactive login
   * @param {Boolean} short Indicates that a short-duration session token is needed, for a few calls at most
   * @returns {Promise<Session>} Session details
   */
  async login ({ name, password, reCaptchaToken, short } = {}) {
    try {
      const url = this.endpoint('login')
      const method = 'post'
      const data = {
        name,
        password,
        reCaptchaToken,
        short
      }

      const { session, message } = await this.request({ url, method, data, retry: false, authorization: false })
      const result = { session: this.asSession(session), message }

      return result

    } catch (error) {
      if (this.isAuthenticationError(error)) {
        const { message, reason } = this.getErrorData(error, 'Login failed') || { message: 'Login failed', reason: 'unknown' }
        return { message, reason }
      } else {
        this.handleError(error)
      }
    }
  }

  /**
   * Logs in to the specified account on behalf of its admin
   * @param {Account} account Account to log in to
   * @param {Boolean} noReturn If true, returning back to the currently logged in account
   * won't be possible, full logout/login will be required instead
   * @returns {Promise<Session>} Session details
   */
  async loginAs ({ account, noReturn } = {}) {
    try {
      const url = this.endpoint('login', 'as')
      const method = 'post'
      const data = {
        accountId: account.id,
        noReturn
      }

      const { session, message } = await this.request({ url, method, data, retry: false })
      const result = { session: this.asSession(session), message }

      return result
    } catch (error) {
      if (this.isAuthenticationError(error)) {
        const { message, reason } = this.getErrorData(error, 'Login failed') || { message: 'Login failed', reason: 'unknown' }
        return { message, reason }
      } else {
        this.handleError(error)
      }
    }
  }

  /**
   * Reauthenticates the existing session
   * @param {String} token Session token
   * @returns {Promise}
   */
  async relogin ({ token } = {}) {
    try {
      const url = this.endpoint('relogin')
      const method = 'post'
      const headers = await APISession.getAuthorizationHeaders(token)
      const data = {
        token: headers['Authorization']
      }

      const { session, message } = await this.request({ url, method, data, headers, retry: false })
      const result = { session: this.asSession(session), message }

      return result
    } catch (error) {
      if (this.isAuthenticationError(error)) {
        const { message, reason } = this.getErrorData(error, 'Login failed') || { message: 'Login failed', reason: 'unknown' }
        return { message, reason }
      } else {
        this.handleError(error)
      }
    }
  }

  /**
   * Checks whether the current account is still logged in
   * @returns {Promise<Boolean>}
   */
  async isLoggedIn () {
    try {
      const url = this.endpoint('login')
      await this.request({ url, params: { noTokenRefresh: true } })
    } catch (error) {
      if (this.isAuthenticationError(error)) {
        return false
      } else {
        this.handleError(error)
      }
    }
  }

  /**
   * Closes account session
   * @param {String} name Account name
   * @returns {Promise}
   */
  async logout ({ name } = {}) {
    try {
      const url = this.endpoint('login')
      const method = 'delete'
      const data = { name }
      const { message } = await this.request({ url, method, data, retry: false })
      return { message }
    } catch (error) {
      this.handleError(error)
    }
  }

  /**
   * Retrieves the specified accounts request
   * @param {String} token Request token
   * @param {Boolean} geo Determine geographic position of the client requesting the data
   * @returns {Promise<SecurityRequest>}
   */
  async getSecurityRequest ({ token, geo } = {}) {
    try {
      const url = this.endpoint('request', token)
      const method = 'get'
      const params = { geo }
      const { request } = await this.request({ url, method, params }) || {}
      return this.asSecurityRequest(request)
    } catch (error) {
      this.handleError(error)
    }
  }

  /**
   * Re-sends account invitation email to the specified account
   * @param {Number} id Account identifier
   * @returns {Promise<Account>} Invited account
   */
  async inviteAccount ({ id } = {}) {
    try {
      const url = this.endpoint('account', id, 'invite')
      const method = 'put'
      const data = { id }
      const { account } = await this.request({ url, method, data })
      return this.asAccount(account)
    } catch (error) {
      this.handleError(error)
    }
  }

  /**
   * Initiates the signup process
   * @param {String} name Account name (e-mail address)
   * @param {Boolean} sendEmail If true, e-mail is sent to the account, asking him to set his new password
   * @param {Boolean} subscribeNewsletter If true, the user subscribes to newsletters
   * @param {String} reCaptchaToken reCaptcha token obtained from Google reCaptcha service during interactive login
   * @returns {Promise<String>} Signup request token
   */
  async signup ({ name, sendEmail, subscribeNewsletter, reCaptchaToken } = {}) {
    try {
      const url = this.endpoint('account', 'signup')
      const method = 'post'
      const data = { name, sendEmail, subscribeNewsletter, reCaptchaToken }
      const { token, pendingRequest, timeToExpire, account } = await this.request({ url, method, data })
      return { token, pendingRequest, timeToExpire, account }
    } catch (error) {
      this.handleError(error)
    }
  }

  /**
   * Activates the specified account
   * @param {String} token Activation request token
   * @param {Account} account Account details
   * @param {Boolean} sendEmail If true, e-mail is sent to the account, notifying him that the account has been activated
   * @param {String} reCaptchaToken reCaptcha token obtained from Google reCaptcha service
   * @returns {Promise<Account>} Activated account
   */
  async activateAccount ({ token, account, sendEmail, reCaptchaToken } = {}) {
    try {
      const url = this.endpoint('account', 'activate', token)
      const method = 'put'
      const data = { account, sendEmail, reCaptchaToken }
      const { account: activatedAccount } = await this.request({ url, method, data })
      return this.asAccount(activatedAccount)
    } catch (error) {
      this.handleError(error)
    }
  }

  /**
   * Resets account password
   * @param {String} name Account name
   * @param {Boolean} sendEmail If true, e-mail is sent to the account, asking him to set his new password
   * @param {String} reCaptchaToken reCaptcha token obtained from Google reCaptcha service during interactive login
   * @returns {Promise}
   */
  async resetPassword ({ name, sendEmail, reCaptchaToken } = {}) {
    try {
      const url = this.endpoint('account', name, 'reset-password')
      const method = 'put'
      const data = { sendEmail, reCaptchaToken }
      const { success, token, mailSent, pendingRequest, timeToExpire, message } = await this.request({ url, method, data, retry: false })
      return { success, token, mailSent, pendingRequest, timeToExpire, message }
    } catch (error) {
      this.handleError(error)
    }
  }

  /**
   * Changes account password
   * @param {String} name Account name
   * @param {String} token Password reset request token, required if call is executed from unauthenticated context
   * @param {String} password New password
   * @param {String} reCaptchaToken reCaptcha token obtained from Google reCaptcha service during interactive login
   * @param {Boolean} sendEmail If true, e-mail is sent to the account, confirming the change of the password
   * @returns {Promise}
   */
  async changePassword ({ name, token, password, sendEmail, reCaptchaToken } = {}) {
    try {
      const url = this.endpoint('account', name, 'password')
      const method = 'put'
      const data = { token, password, sendEmail, reCaptchaToken }
      const { mailSent, message } = await this.request({ url, method, data, retry: false })
      return { mailSent, message }
    } catch (error) {
      this.handleError(error)
    }
  }

  /**
   * Verfies Google reCaptcha token
   * @param {String} token Token to verify
   * @param {String} secret Secret key for token verification
   * @returns {Promise}
   */
  async validateReCaptcha ({ token, secret } = {}) {
    if (!token) throw new Error('Token is required')
    if (!secret) throw new Error('Secret key is required')

    const url = 'https://www.google.com/recaptcha/api/siteverify'

    try {
      const data = new URLSearchParams()
      data.append('secret', secret)
      data.append('response', token)
      const options = {
        url,
        method: 'post',
        headers: {
          'content-type': 'application/x-www-form-urlencoded'
        },
        body: data
      }

      const response = await this.rawRequest(options)
      const { success, challenge_ts, hostname, score, action, 'error-codes': errors = [] } = response
      const error = errors.length === 0 ? undefined : errors.join(', ')

      return {
        success,
        challenge_ts,
        hostname,
        score,
        action,
        error
      }

    } catch (error) {
      Log.error('reCaptcha token verification failed with error')
      Log.error('reCaptcha endpoint:', url)
      Log.exception(error)
      return { error: error.message }
    }
  }

  /**
   * Sets account language and/or country
   * @param {Account} account Account to update
   * @param {String} countryCode ISO 3166 Alpha-2 country code
   * @param {String} languageCode ISO 639-2 language code
   * @param {String} timezone Name of country timezone, as per https://www.iana.org/time-zones
   * @returns {Promise<Account>} The updated account
   */
  async setLocale ({ account, countryCode, languageCode, timezone } = {}) {
    try {
      const url = this.endpoint('account', account.id, 'locale')
      const method = 'put'
      const data = { countryCode, languageCode, timezone }
      const result = await this.request({ url, method, data })
      return this.asAccount(result?.account)
    } catch (error) {
      this.handleError(error)
    }
  }

  /**
   * Updates the specified permissions of a account
   * @param {Account} account Account to update permissions
   * @param {Array[Permission]} permissions Permissions to grant or deny
   * @returns {Promise<Account>} The updated account
   */
  async updatePermissions ({ account, allow: permissions } = {}) {
    if (account && permissions?.length > 0) {
      try {
        const url = this.endpoint('permissions', account.id)
        const method = 'put'
        const data = {
          account: { id: account.id },
          permissions: permissions.map(p => new Permission(p).getCore())
        }
        const result = await this.request({ url, method, data })
        return result?.account
      } catch (error) {
        this.handleError(error)
      }
    } else {
      return account
    }
  }

  /**
   * Converts the specified data item to Feature instance
   * @param {Object} item Data item
   * @returns {Feature}
   */
  asFeature (item) {
    if (item) {
      const features = (item.features || []).map(feature => this.asFeature(feature))
      return new Feature({ ...item, features })
    }
  }

  /**
   * Converts the specified data item Account instance
   * @param {Object} item Data item
   * @returns {Account}
   */
  asAccount (item) {
    if (item) {
      return new Account(item)
    }
  }

  /**
   * Converts the specified data items to Account instance
   * @param {Array} item Data items
   * @returns {Array[Account]}
   */
  asAccounts (items = []) {
    return items.map(item => this.asAccount(item))
  }

  /**
   * Converts the specified data item to Session instance
   * @param {Object} item Data item
   * @returns {Session}
   */
  asSession (item) {
    if (item) {
      return new Session({ ...item })
    }
  }

  /**
   * Converts the specified data item to SecurityRequest instance
   * @param {Object} item Data item
   * @returns {SecurityRequest}
   */
  asSecurityRequest (item) {
    if (item) {
      return SecurityRequest.create(item)
    }
  }

  /**
   * Retrieves all preferences of the specified owner or global
   * @param {Object} [options]
   * @param {Entity|Account|Avatar|Object|Number} [options.owner] Preferences owner (account, avatar, etc.). If not specified, global preferences are returned
   * @param {String} [options.prefix] Prefix filter
   * @returns {Promise<Preference[]>}
   */
  async getPreferences ({ owner, prefix } = {}) {
    try {
      const method = 'get'
      const ownerId = owner?.id ?? owner
      const url = ownerId != null
        ? this.endpoint('owner', ownerId, 'preferences')
        : this.endpoint('preferences')
      const params = prefix ? { prefix } : undefined
      const { preferences } = await this.request({ method, url, params })
      return this.asPreferences(preferences)
    } catch (error) {
      this.handleError(error)
      return []
    }
  }

  /**
   * Retrieves preferences dictionary of the specified owner or global
   * @param {Object} [options]
   * @param {Entity|Account|Avatar|Object|Number} [options.owner] Preferences owner. If not specified, global preferences are returned
   * @param {String} [options.prefix] Prefix filter
   * @returns {Promise<Preferences>}
   */
  async getPreferencesDictionary ({ owner, prefix } = {}) {
    try {
      const preferences = await this.getPreferences({ owner, prefix })
      return this.asPreferencesDictionary(preferences)
    } catch (error) {
      this.handleError(error)
      return new Preferences()
    }
  }

  /**
   * Retrieves a single preference
   * @param {Object} options
   * @param {Entity|Account|Avatar|Object|Number} [options.owner] Preferences owner. If not specified, global preference is returned
   * @param {String} options.name Preference name
   * @param {any} [options.defaultValue] Default value if preference not found
   * @returns {Promise<Preference>}
   */
  async getPreference ({ owner, name, defaultValue } = {}) {
    try {
      const method = 'get'
      const ownerId = owner?.id ?? owner
      const url = ownerId != null
        ? this.endpoint('owner', ownerId, 'preferences', name)
        : this.endpoint('preferences', name)
      const params = defaultValue !== undefined ? { defaultValue } : undefined
      const { preference } = await this.request({ method, url, params })
      return this.asPreference(preference)
    } catch (error) {
      this.handleError(error)
    }
  }

  /**
   * Saves a single preference
   * @param {Object} options
   * @param {Preference|Object} options.preference Preference to save
   * @param {Entity|Account|Avatar|Object|Number} [options.owner] Preferences owner. Don't specify if saving a global preference
   * @returns {Promise<Preference>}
   */
  async savePreference ({ preference, owner } = {}) {
    try {
      const saved = await this.savePreferences({ preferences: [preference], owner })
      return saved?.[0]
    } catch (error) {
      Log.exception(error)
    }
  }

  /**
   * Saves preferences
   * @param {Object} options
   * @param {Preference[]|Object[]} options.preferences Preferences to save
   * @param {Entity|Account|Avatar|Object|Number} [options.owner] Preferences owner. Don't specify if saving global preferences
   * @returns {Promise<Preference[]>}
   */
  async savePreferences ({ preferences = [], owner } = {}) {
    try {
      const method = 'put'
      const ownerId = owner?.id ?? owner ?? preferences[0]?.ownerId ?? preferences[0]?.accountId
      const isGlobal = !ownerId
      const data = {
        isGlobal,
        preferences: preferences.map(item => {
          const pref = item instanceof Preference ? item : new Preference(item)
          return {
            name: pref.name,
            type: pref.type,
            value: pref.serializedValue
          }
        })
      }
      const url = ownerId != null
        ? this.endpoint('owner', ownerId, 'preferences')
        : this.endpoint('preferences')
      const result = await this.request({ method, url, data })
      return this.asPreferences(result?.preferences)
    } catch (error) {
      Log.exception(error)
      return []
    }
  }

  /**
   * Converts the specified data item to Preference instance
   * @param {Object} item Data item
   * @returns {Preference}
   */
  asPreference (item) {
    if (item) {
      return new Preference(item)
    }
  }

  /**
   * Converts the specified data items to Preference instances
   * @param {Array} items Data items
   * @returns {Array[Preference]}
   */
  asPreferences (items = []) {
    return (items || []).filter(item => item != null).map(item => this.asPreference(item))
  }

  /**
   * Converts the specified data items to Preferences dictionary
   * @param {Array} items Data items
   * @returns {Preferences}
   */
  asPreferencesDictionary (items = []) {
    if (items) {
      return (items || []).filter(item => item != null).reduce((all, item) => {
        const preference = item instanceof Preference ? item : new Preference(item)
        all[preference.name] = preference.value
        return all
      }, new Preferences())
    }
  }
}
